import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabView, TabPanel } from 'primereact/tabview';
import { Tooltip } from 'primereact/tooltip';
import { confirmDialog } from 'primereact/confirmdialog';
import ApiService, { handleApiError } from '../../services/api';
import { FeeSchedule } from '../../types';
import FeeScheduleUpload from './FeeScheduleUpload';
import { useToast } from '../../context/ToastContext';
import { usePracticeData } from '../../hooks/usePracticeData';

// Normalize a payer name for matching (lowercase + strip spaces). Mirrors how the payer
// mix / drug-payer matrix keys line up with fee-schedule row names.
const normPayer = (s: string): string => String(s || '').trim().toLowerCase().replace(/\s+/g, '');

interface FeeScheduleModalProps {
  visible: boolean;
  accountNumber: string;
  masterPayorGroups: string[];
  onHide: () => void;
}

interface CategoryGroup {
  category: string;
  items: FeeSchedule[];
}

const FeeScheduleModal: React.FC<FeeScheduleModalProps> = ({
  visible,
  accountNumber,
  masterPayorGroups,
  onHide,
}) => {
  const { isDemoMode } = usePracticeData();
  const [feeSchedules, setFeeSchedules] = useState<FeeSchedule[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editedRates, setEditedRates] = useState<Record<string, number>>({});
  const [originalRates, setOriginalRates] = useState<Record<string, number>>({});
  const [activeTab, setActiveTab] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState('');
  const [uploadHasChanges, setUploadHasChanges] = useState(false);
  // Payer preferences (drug-payer matrix) per category -> pfId -> payer -> checked.
  // Powers the green/red "checked in payer preferences" dot next to each payer name.
  const [prefsByCategory, setPrefsByCategory] = useState<Record<string, Record<string, Record<string, boolean>>>>({});
  const [prefsLoaded, setPrefsLoaded] = useState<Record<string, boolean>>({});
  const deleteDialogOpenRef = useRef(false);
  // PrimeReact's Tooltip only binds its target listeners from its SECOND render
  // onward (loadTargetEvents sits in a useUpdateEffect), so a Tooltip that mounts
  // in the same commit as its targets never attaches. Held so we can rebind by hand.
  const payerTipRef = useRef<Tooltip>(null);
  const toast = useToast();

  const loadFeeSchedules = useCallback(async () => {
    if (!accountNumber) {
      setError('No active account selected.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await ApiService.getFeeSchedules(accountNumber);
      setFeeSchedules(response.fee_schedules);
      setSelectedId(null);
      setEditedRates({});
      setOriginalRates({});
      setExpandedCategories(new Set());
    } catch (err) {
      setFeeSchedules([]);
      setSelectedId(null);
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, [accountNumber]);

  useEffect(() => {
    if (!visible || !accountNumber) return;
    loadFeeSchedules();
    setCategoryFilter('');
    setActiveTab(0);
    setUploadHasChanges(false);
  }, [visible, accountNumber, loadFeeSchedules]);

  const selectedSchedule = useMemo(
    () => feeSchedules.find((fs) => fs.id === selectedId) ?? null,
    [feeSchedules, selectedId]
  );

  // Load the drug-payer matrix ("payer preferences") for the selected PF's category once,
  // cached per category. Drives the checked/unchecked dot next to each payer.
  useEffect(() => {
    const cat = selectedSchedule?.category;
    if (!cat || !accountNumber || prefsLoaded[cat]) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await ApiService.getDrugPayerMatrix(accountNumber, cat);
        if (!cancelled) setPrefsByCategory((prev) => ({ ...prev, [cat]: resp?.drugPayerMatrix || {} }));
      } catch {
        if (!cancelled) setPrefsByCategory((prev) => ({ ...prev, [cat]: {} }));
      } finally {
        if (!cancelled) setPrefsLoaded((prev) => ({ ...prev, [cat]: true }));
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSchedule?.category, accountNumber, prefsLoaded]);

  // Normalized payer -> checked lookup for the selected PF. null until preferences load
  // (so we don't show a misleading red before we know). A payer absent from the matrix is
  // checked by default, matching the app's default-eligible behaviour.
  const payerCheckedLookup = useMemo(() => {
    const cat = selectedSchedule?.category;
    const pid = selectedSchedule?.product_family_id;
    if (!cat || pid == null || !prefsLoaded[cat]) return null;
    const pfRow = (prefsByCategory[cat] || {})[String(pid)] || {};
    const map = new Map<string, boolean>();
    for (const [payer, val] of Object.entries(pfRow)) {
      map.set(normPayer(payer), val !== false);
    }
    return map;
  }, [selectedSchedule, prefsByCategory, prefsLoaded]);

  const hasChanges = useMemo(() => {
    // Compare by value, treating a missing key and $0 as equivalent (both mean "no rate ->
    // Medicare fallback"). Rows we surface for unpriced payers start absent/0, so without
    // this normalization they'd falsely register as changes the moment they render.
    const allKeys = Object.keys(originalRates).concat(Object.keys(editedRates));
    return allKeys.some((k) => (originalRates[k] ?? 0) !== (editedRates[k] ?? 0));
  }, [editedRates, originalRates]);

  const filteredPayers = useMemo(() => {
    // Show every payer that can carry weight, not just the ones already saved on THIS PF.
    // A payer like "Blue Cross Blue Shield (Choice)" may not be a registered master payor
    // group yet still be priced on sibling product families — if this PF's schedule was
    // created without that column, the gap is otherwise invisible and un-fixable here.
    // Union three sources, dedupe by normalized name (saved spelling/value wins):
    //   1. account-wide master payor groups
    //   2. every payer priced on any PF IN THE SAME CATEGORY (surfaces siblings' columns)
    //   3. this PF's own saved rates
    const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, '');
    const byNorm = new Map<string, string>();
    for (const g of masterPayorGroups) {
      const k = norm(g);
      if (k && !byNorm.has(k)) byNorm.set(k, g);
    }
    const cat = selectedSchedule?.category;
    if (cat) {
      for (const fs of feeSchedules) {
        if (fs.category !== cat) continue;
        for (const key of Object.keys(fs.reimbursements || {})) {
          const k = norm(key);
          if (k && !byNorm.has(k)) byNorm.set(k, key);
        }
      }
    }
    for (const saved of Object.keys(editedRates)) {
      const k = norm(saved);
      if (k) byNorm.set(k, saved); // a saved rate on THIS PF wins the display spelling
    }
    const payers = Array.from(byNorm.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
    if (!filterText.trim()) return payers;
    const lower = filterText.toLowerCase();
    return payers.filter((p) => p.toLowerCase().includes(lower));
  }, [editedRates, masterPayorGroups, feeSchedules, selectedSchedule, filterText]);

  // Rebind the payer-preference dot / Medicare-fallback pill tooltips whenever the
  // rendered set changes. Without this they are dead on any render where the Tooltip
  // mounts alongside its targets — e.g. reopening the modal and selecting a product
  // family in a category whose preferences are already cached in state.
  useEffect(() => {
    payerTipRef.current?.updateTargetEvents();
  }, [filteredPayers, payerCheckedLookup, editedRates, selectedId]);

  // Build category groups, filtered by categoryFilter
  const categoryGroups = useMemo((): CategoryGroup[] => {
    const groupMap: Record<string, FeeSchedule[]> = {};
    for (const fs of feeSchedules) {
      // Skip fee schedules with no category — "Uncategorized" entries don't make
      // sense to edit yet, so they're excluded from the options.
      const cat = fs.category;
      if (!cat) continue;
      if (!groupMap[cat]) groupMap[cat] = [];
      groupMap[cat].push(fs);
    }
    const groups = Object.entries(groupMap)
      .map(([category, items]) => ({
        category,
        items: items.sort((a, b) =>
          a.product_family_name.localeCompare(b.product_family_name, undefined, { sensitivity: 'base' })
        ),
      }))
      .sort((a, b) => a.category.localeCompare(b.category, undefined, { sensitivity: 'base' }));

    if (!categoryFilter.trim()) return groups;
    const lower = categoryFilter.toLowerCase();
    return groups.filter((g) => g.category.toLowerCase().includes(lower));
  }, [feeSchedules, categoryFilter]);

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const guardAndSelect = (nextId: number) => {
    if (nextId === selectedId) return;
    const doSelect = () => {
      const next = feeSchedules.find((fs) => fs.id === nextId);
      if (next) {
        setSelectedId(next.id);
        setEditedRates({ ...next.reimbursements });
        setOriginalRates({ ...next.reimbursements });
        setFilterText('');
      }
    };
    if (hasChanges) {
      confirmDialog({
        message: 'You have unsaved changes. Discard them?',
        header: 'Unsaved Changes',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Discard',
        rejectLabel: 'Stay',
        acceptClassName: 'p-button-danger',
        accept: doSelect,
      });
    } else {
      doSelect();
    }
  };

  const handleRateChange = (payer: string, value: number | null) => {
    setEditedRates((prev) => ({ ...prev, [payer]: value ?? 0 }));
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setIsSaving(true);
    try {
      await ApiService.updateFeeSchedule(selectedId, editedRates);
      setFeeSchedules((prev) =>
        prev.map((fs) =>
          fs.id === selectedId ? { ...fs, reimbursements: { ...editedRates } } : fs
        )
      );
      setOriginalRates({ ...editedRates });
      toast.current?.show({
        severity: 'success',
        summary: 'Saved',
        detail: 'Rates saved successfully.',
        life: 3000,
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Save Failed',
        detail: handleApiError(err),
        life: 4000,
      });
      setEditedRates({ ...originalRates });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setEditedRates({ ...originalRates });
  };

  const handleDelete = () => {
    if (!selectedSchedule || deleteDialogOpenRef.current) return;
    deleteDialogOpenRef.current = true;
    confirmDialog({
      message: `Clear fee schedule rates for "${selectedSchedule.product_family_name}"?`,
      header: 'Clear Fee Schedule',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        deleteDialogOpenRef.current = false;
        try {
          await ApiService.deleteFeeSchedule(selectedSchedule.id);
          // Backend does a soft clear (sets reimbursements to {}) — keep the row in
          // the list so the UI stays consistent with what the DB actually contains.
          setFeeSchedules((prev) =>
            prev.map((fs) =>
              fs.id === selectedSchedule.id ? { ...fs, reimbursements: {} } : fs
            )
          );
          setEditedRates({});
          setOriginalRates({});
          setFilterText('');
          toast.current?.show({
            severity: 'info',
            summary: 'Rates Cleared',
            detail: `Fee schedule rates cleared for "${selectedSchedule.product_family_name}".`,
            life: 3000,
          });
        } catch (err) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: handleApiError(err),
            life: 4000,
          });
        }
      },
      reject: () => { deleteDialogOpenRef.current = false; },
    });
  };

  const handleTabChange = (index: number) => {
    // Switching away from Upload tab while there's a staged upload.
    if (activeTab === 1 && index !== 1 && uploadHasChanges) {
      confirmDialog({
        message: 'You have a staged fee schedule upload. Discard it and switch tabs?',
        header: 'Discard Upload',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Discard',
        rejectLabel: 'Stay',
        acceptClassName: 'p-button-danger',
        accept: () => {
          // Switching away unmounts the Upload tab (TabView renderActiveOnly),
          // which discards the staged upload. Clear the mirror flag too so a
          // later close from another tab doesn't wrongly prompt about staging.
          setUploadHasChanges(false);
          setActiveTab(index);
        },
      });
      return;
    }
    // Switching away from Manage Rates tab while there are unsaved rate edits.
    if (activeTab === 0 && index !== 0 && hasChanges) {
      confirmDialog({
        message: 'You have unsaved rate changes. Discard them?',
        header: 'Unsaved Changes',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Discard',
        rejectLabel: 'Stay',
        acceptClassName: 'p-button-danger',
        accept: () => {
          setEditedRates({ ...originalRates });
          setActiveTab(index);
        },
      });
      return;
    }
    setActiveTab(index);
  };

  const handleHide = () => {
    const dirty = hasChanges || uploadHasChanges;
    if (dirty) {
      confirmDialog({
        message: uploadHasChanges && !hasChanges
          ? 'You have a staged fee schedule upload. Discard it and close?'
          : 'You have unsaved changes. Discard them and close?',
        header: 'Unsaved Changes',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Discard & Close',
        rejectLabel: 'Stay',
        acceptClassName: 'p-button-danger',
        accept: onHide,
      });
    } else {
      onHide();
    }
  };

  return (
    <Dialog
      header="Fee Schedules"
      visible={visible}
      style={{ width: '70vw', maxWidth: '1000px', height: activeTab === 0 ? '80vh' : undefined }}
      contentStyle={{ overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' }}
      className="fee-schedule-dialog"
      modal
      onHide={handleHide}
      footer={
        activeTab !== 0 ? null : (
        <div className="flex justify-content-between align-items-center" style={{ paddingTop: '0.75rem' }}>
          <div>
            {selectedSchedule && (
              // Tooltip on the span, not showOnDisabled on the Button: showOnDisabled
              // re-parents the button outside React and crashes on unmount.
              <>
                <Tooltip target=".demo-tip-fs-clear" position="top" />
                <span
                  className={`inline-flex${isDemoMode ? ' demo-tip-fs-clear' : ''}`}
                  data-pr-tooltip={isDemoMode ? 'Read-only demo account' : undefined}
                >
                  <Button
                    label="Clear Rates"
                    icon="pi pi-trash"
                    severity="danger"
                    text
                    style={{ color: 'var(--red-500)' }}
                    onClick={handleDelete}
                    // Demo accounts are read-only server-side (423).
                    disabled={isLoading || isSaving || isDemoMode || Object.keys(selectedSchedule.reimbursements).length === 0}
                  />
                </span>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              label="Reset"
              icon="pi pi-undo"
              severity="secondary"
              text
              onClick={handleReset}
              disabled={!hasChanges || isSaving}
            />
            <Tooltip target=".demo-tip-fs-save" position="top" />
            <span
              className={`inline-flex${isDemoMode ? ' demo-tip-fs-save' : ''}`}
              data-pr-tooltip={isDemoMode ? 'Read-only demo account' : undefined}
            >
              <Button
                label="Save"
                icon={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-save'}
                onClick={handleSave}
                // Demo accounts are read-only server-side (423).
                disabled={isLoading || isSaving || !hasChanges || isDemoMode}
              />
            </span>
          </div>
        </div>
        )
      }
    >
      <TabView activeIndex={activeTab} onTabChange={(e) => handleTabChange(e.index)}>
        <TabPanel header="Manage Rates">
          {isLoading && (
            <div className="fee-schedule-status">
              <ProgressSpinner style={{ width: '36px', height: '36px' }} strokeWidth="6" />
            </div>
          )}

          {!isLoading && error && (
            <div className="fee-schedule-status fee-schedule-status-error">{error}</div>
          )}

          {!isLoading && !error && feeSchedules.length === 0 && (
            <div className="fee-schedule-status fee-schedule-empty-state">
              <i className="pi pi-inbox" style={{ fontSize: '2rem', color: '#9ca3af' }} />
              <p className="fee-schedule-empty-title">No fee schedules set up for this account yet</p>
              <p className="fee-schedule-empty-sub">
                To add rates, switch to the <strong>Upload Fee Schedule</strong> tab and upload your
                fee schedule. Once uploaded, your product families will appear here to review and edit.
              </p>
              <Button
                label="Go to Upload Fee Schedule"
                icon="pi pi-upload"
                onClick={() => setActiveTab(1)}
                className="mt-1"
              />
            </div>
          )}

          {!isLoading && !error && feeSchedules.length > 0 && (
            <div className="fee-schedule-layout">
              {/* ---- Sidebar: category-grouped tree ---- */}
              <div className="fee-schedule-sidebar">
                <div className="fee-schedule-sidebar-search">
                  <InputText
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    placeholder="Search categories..."
                    className="w-full p-inputtext-sm"
                  />
                </div>

                <div className="fee-schedule-tree">
                  {categoryGroups.length === 0 && (
                    <div className="fee-schedule-status" style={{ padding: '0.5rem 0' }}>
                      No categories match.
                    </div>
                  )}
                  {categoryGroups.map((group) => {
                    const isExpanded = expandedCategories.has(group.category);
                    return (
                      <div key={group.category} className="fee-schedule-category-group">
                        <button
                          type="button"
                          className="fee-schedule-category-header"
                          onClick={() => toggleCategory(group.category)}
                          aria-expanded={isExpanded}
                        >
                          <i className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'} fee-schedule-chevron`} />
                          <span className="fee-schedule-category-label">{group.category}</span>
                        </button>
                        {isExpanded && (
                          <div className="fee-schedule-category-items">
                            {group.items.map((fs) => (
                              <button
                                key={fs.id}
                                type="button"
                                className={`fee-schedule-item${fs.id === selectedId ? ' fee-schedule-item-selected' : ''}`}
                                onClick={() => guardAndSelect(fs.id)}
                              >
                                {fs.product_family_name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ---- Detail panel ---- */}
              <div className="fee-schedule-detail">
                {/* Mounted unconditionally so it is past its bind-skipping first
                    render before any dot or pill exists; see payerTipRef. */}
                <Tooltip ref={payerTipRef} target=".payer-pref-dot,.fee-fallback-pill" />
                {!selectedSchedule && (
                  <div className="fee-schedule-empty-detail">
                    <i className="pi pi-arrow-left" style={{ fontSize: '1.25rem', color: '#9ca3af' }} />
                    <span>Select a product family to view its rates</span>
                  </div>
                )}
                {selectedSchedule && (
                  <>
                    <div className="fee-schedule-detail-header">
                      <h3 className="m-0">{selectedSchedule.product_family_name}</h3>
                      <span className="text-500 text-sm">Reimbursement rates per Billing Unit</span>
                    </div>

                    <div className="fee-schedule-filter">
                      <InputText
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        placeholder="Filter payers..."
                        className="w-full p-inputtext-sm"
                      />
                    </div>

                    <div className="fee-schedule-rates">
                      {filteredPayers.length === 0 && (
                        <div className="fee-schedule-status">
                          {filterText.trim() ? 'No payers match the filter.' : 'No rates defined for this product family.'}
                        </div>
                      )}
                      {filteredPayers.map((payer) => {
                        const val = editedRates[payer];
                        const isFallback = val === undefined || val === null || val === 0;
                        // Checked/unchecked in payer preferences for the selected PF.
                        // null = preferences not loaded yet -> no dot shown.
                        const checked = payerCheckedLookup
                          ? (payerCheckedLookup.get(normPayer(payer)) ?? true)
                          : null;
                        return (
                          <div className="fee-schedule-rate-row" key={payer}>
                            <span className="fee-schedule-payer-name">
                              {checked !== null && (
                                <span
                                  className="payer-pref-dot"
                                  data-pr-tooltip={checked
                                    ? 'Checked in payer preferences for this product family'
                                    : 'Unchecked in payer preferences — excluded for this product family'}
                                  data-pr-position="top"
                                  style={{
                                    display: 'inline-block',
                                    width: 9,
                                    height: 9,
                                    borderRadius: '50%',
                                    marginRight: '0.5rem',
                                    verticalAlign: 'middle',
                                    flexShrink: 0,
                                    background: checked ? 'rgba(34,197,94,0.28)' : 'rgba(239,68,68,0.22)',
                                    border: checked ? '1px solid rgba(22,163,74,0.55)' : '1px solid rgba(220,38,38,0.5)',
                                    cursor: 'default',
                                  }}
                                />
                              )}
                              {payer}
                              {isFallback && (
                                <span
                                  className="fee-fallback-pill"
                                  data-pr-tooltip="No rate — this payer falls back to the Medicare rate in weighted NCR"
                                  data-pr-position="top"
                                  style={{
                                    marginLeft: '0.4rem',
                                    fontSize: '0.65rem',
                                    padding: '0.05rem 0.45rem',
                                    borderRadius: '999px',
                                    background: '#fef3c7',
                                    color: '#92400e',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  ⚠ Medicare fallback
                                </span>
                              )}
                            </span>
                            <InputNumber
                              value={editedRates[payer] ?? 0}
                              onValueChange={(e) => handleRateChange(payer, e.value ?? null)}
                              mode="currency"
                              currency="USD"
                              locale="en-US"
                              minFractionDigits={2}
                              maxFractionDigits={4}
                              min={0}
                              max={999999.9999}
                              className="fee-schedule-rate-input"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </TabPanel>

        <TabPanel header="Upload Fee Schedule">
          <FeeScheduleUpload
            accountNumber={accountNumber}
            masterPayorGroups={masterPayorGroups}
            existingFeeSchedules={feeSchedules}
            onCommitted={loadFeeSchedules}
            onHasStagedChanges={setUploadHasChanges}
          />
        </TabPanel>
      </TabView>
    </Dialog>
  );
};

export default FeeScheduleModal;
